import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OutlineView = ({ outline, onReorder, onUpdateSection }) => {
  const [expandedSections, setExpandedSections] = useState(new Set([0]));
  const [editingSection, setEditingSection] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');

  const toggleSection = (index) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(index)) {
      newExpanded?.delete(index);
    } else {
      newExpanded?.add(index);
    }
    setExpandedSections(newExpanded);
  };

  const handleEditStart = (section, index) => {
    setEditingSection(index);
    setEditedTitle(section?.title);
  };

  const handleEditSave = (index) => {
    onUpdateSection(index, editedTitle);
    setEditingSection(null);
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < outline?.length) {
      onReorder(index, newIndex);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-heading font-semibold text-foreground">
          Book Outline
        </h2>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
        >
          Add Section
        </Button>
      </div>
      <div className="divide-y divide-border">
        {outline?.map((section, index) => {
          const isExpanded = expandedSections?.has(index);
          const isEditing = editingSection === index;

          return (
            <div key={section?.id} className="p-4 hover:bg-muted/30 transition-colors duration-200">
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    onClick={() => moveSection(index, 'up')}
                    disabled={index === 0}
                    className="p-1 hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                    aria-label="Move up"
                  >
                    <Icon name="ChevronUp" size={14} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => moveSection(index, 'down')}
                    disabled={index === outline?.length - 1}
                    className="p-1 hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                    aria-label="Move down"
                  >
                    <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                  </button>
                </div>

                <button
                  onClick={() => toggleSection(index)}
                  className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors duration-200"
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <Icon 
                    name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
                    size={18} 
                    className="text-muted-foreground"
                  />
                </button>

                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e?.target?.value)}
                        className="flex-1 px-3 py-1.5 bg-background border border-border rounded-md text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                      />
                      <Button
                        variant="default"
                        size="xs"
                        iconName="Check"
                        onClick={() => handleEditSave(index)}
                      />
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="X"
                        onClick={() => setEditingSection(null)}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base font-heading font-semibold text-foreground">
                        {section?.number}. {section?.title}
                      </h3>
                      <button
                        onClick={() => handleEditStart(section, index)}
                        className="p-1 hover:bg-muted rounded transition-colors duration-200"
                        aria-label="Edit section"
                      >
                        <Icon name="Edit2" size={14} className="text-muted-foreground" />
                      </button>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-1">
                      <Icon name="FileText" size={12} />
                      {section?.wordCount?.toLocaleString()} words
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="List" size={12} />
                      {section?.subsections?.length} subsections
                    </span>
                    {section?.isComplete && (
                      <span className="flex items-center gap-1 text-success">
                        <Icon name="CheckCircle" size={12} />
                        Complete
                      </span>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="mt-3 pl-4 border-l-2 border-border space-y-2 animate-fade-in">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {section?.description}
                      </p>

                      {section?.subsections?.length > 0 && (
                        <div className="space-y-1.5 mt-3">
                          {section?.subsections?.map((subsection, subIndex) => (
                            <div
                              key={subIndex}
                              className="flex items-start gap-2 p-2 bg-muted/30 rounded-md"
                            >
                              <Icon name="Circle" size={8} className="text-primary mt-1.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground">
                                  {subsection?.title}
                                </p>
                                {subsection?.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {subsection?.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {section?.keyPoints && section?.keyPoints?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-foreground mb-2">Key Points:</p>
                          <ul className="space-y-1">
                            {section?.keyPoints?.map((point, pointIndex) => (
                              <li key={pointIndex} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <Icon name="Check" size={12} className="text-success mt-0.5 flex-shrink-0" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OutlineView;